import React, { useState, useMemo } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Input } from "../ui/input";
import { Plus, X, Search, Check } from "lucide-react";
import { useTags } from "@/hooks/useTags";
import { normalizeTagName } from "@/lib/tagName";
import { toast } from "sonner";
import ScrollArea from "../ui/shared/ScrollArea";
import { useTranslation } from "react-i18next";

interface CampaignTagSelectorProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  label?: string | null;
}

const CampaignTagSelector: React.FC<CampaignTagSelectorProps> = ({
  selectedTags,
  onTagsChange,
  label = "Tags",
}) => {
  const { t } = useTranslation("automations");
  const { tags: availableTags, createTag } = useTags();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [creatingTag, setCreatingTag] = useState(false);

  const activeTags = useMemo(
    () => availableTags.filter((t) => t.is_active),
    [availableTags]
  );

  const filteredTags = useMemo(() => {
    if (!searchQuery.trim()) return activeTags;
    const q = searchQuery.toLowerCase().trim();
    return activeTags.filter((t) => t.name.toLowerCase().includes(q));
  }, [activeTags, searchQuery]);

  const canCreateNewTag = useMemo(() => {
    if (!searchQuery.trim() || creatingTag) return false;
    const normalized = normalizeTagName(searchQuery);
    return (
      !activeTags.some((t) => t.name.toLowerCase() === normalized) &&
      normalized.length > 0 &&
      normalized.length <= 50
    );
  }, [searchQuery, activeTags, creatingTag]);

  const handleToggleTag = (tagName: string) => {
    const lower = normalizeTagName(tagName);
    if (selectedTags.includes(lower)) {
      onTagsChange(selectedTags.filter((t) => t !== lower));
    } else {
      onTagsChange([...selectedTags, lower]);
    }
  };

  const handleRemoveTag = (tagName: string) => {
    onTagsChange(selectedTags.filter((t) => t !== tagName));
  };

  const handleCreateTag = async () => {
    if (!canCreateNewTag) return;
    setCreatingTag(true);
    try {
      const tagName = normalizeTagName(searchQuery);
      const newTag = await createTag({
        name: tagName,
        color: "#3B82F6",
        is_active: true,
      });
      if (newTag) {
        const name = normalizeTagName(newTag.name);
        onTagsChange(selectedTags.includes(name) ? selectedTags : [...selectedTags, name]);
        setSearchQuery("");
        toast.success(t("campaign.tagCreated", { name }));
      }
    } catch {
      toast.error(t("campaign.tagCreateError"));
    } finally {
      setCreatingTag(false);
    }
  };

  return (
    <div className="flex flex-col w-full items-start gap-3">
      {label && (
        <label className="text-sm font-medium leading-none text-[#0F172A] dark:text-[#FAFAFA]">
          {label}
        </label>
      )}

      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTags.map((tagName) => (
            <div
              key={tagName}
              className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium bg-[#DBEAFE] text-[#2563EB] border border-blue-200 dark:bg-blue-500/20 dark:text-[#60A5FA] dark:border-blue-500/30"
            >
              <span className="capitalize">{tagName}</span>
              <button
                type="button"
                onClick={() => handleRemoveTag(tagName)}
                aria-label={t('common:shared.removeTag', { name: tagName })}
                className="ml-0.5 text-[#2563EB] hover:text-[#DC2626] transition-colors duration-200 dark:text-[#60A5FA] dark:hover:text-[#F87171]"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* modal: sem isso o Radix Dialog que envolve o seletor (campanhas, follow-ups,
          blocos de fluxo) trava a roda do mouse fora do DialogContent e a lista de
          tags fica sem rolagem. */}
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-xl border border-dashed border-[#E5E7EB] text-[#64748B] hover:text-[#2563EB] hover:border-[#2563EB] hover:bg-[#EEF4FF] transition-colors duration-200 dark:border-[#3F3F46] dark:text-[#A1A1AA] dark:hover:text-[#60A5FA] dark:hover:border-[#60A5FA] dark:hover:bg-blue-500/15"
          >
            <Plus size={14} />
            {t("campaign.addTag")}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          collisionPadding={16}
          className="!bg-white border border-[#E5E7EB] p-0 w-[280px] rounded-xl shadow-md flex flex-col max-h-[min(320px,var(--radix-popover-content-available-height))] dark:!bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none"
        >
          <div className="shrink-0 p-3 border-b border-[#E5E7EB] dark:border-[#3F3F46]">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748B] dark:text-[#A1A1AA]" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("campaign.searchOrCreateTag")}
                className="pl-8 h-8 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] text-sm placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                autoFocus
              />
            </div>
          </div>

          <ScrollArea classNames={{ root: "flex-1 min-h-0 overflow-auto" }}>
            <div className="p-1.5 flex flex-col gap-0.5">
              {canCreateNewTag && (
                <button
                  type="button"
                  onClick={handleCreateTag}
                  disabled={creatingTag}
                  className="flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm text-[#16A34A] hover:bg-[#ECFDF5] transition-colors duration-200 disabled:opacity-50 w-full text-left dark:text-[#22C55E] dark:hover:bg-green-500/10"
                >
                  <Plus size={14} />
                  <span>
                    {creatingTag
                      ? "Criando..."
                      : `Criar "${normalizeTagName(searchQuery)}"`}
                  </span>
                </button>
              )}

              {filteredTags.length === 0 && !canCreateNewTag ? (
                <p className="text-sm text-[#64748B] dark:text-[#71717A] px-2.5 py-3 text-center">
                  {t("campaign.noTagFound")}
                </p>
              ) : (
                filteredTags.map((tag) => {
                  const isSelected = selectedTags.includes(
                    normalizeTagName(tag.name)
                  );
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => handleToggleTag(tag.name)}
                      className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm transition-colors duration-200 w-full text-left ${
                        isSelected
                          ? "bg-[#EEF4FF] text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA]"
                          : "text-[#334155] hover:bg-[#F1F5F9] dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]"
                      }`}
                    >
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-[#2563EB] dark:bg-[#60A5FA]" />
                      <span className="flex-1 capitalize truncate">
                        {tag.name}
                      </span>
                      {isSelected && (
                        <Check size={14} className="text-[#2563EB] dark:text-[#60A5FA] flex-shrink-0" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default CampaignTagSelector;
